import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { achievements, getAchievementsForSport } from '@/data/achievements';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePreferredSport } from '@/hooks/usePreferredSport';

const CATEGORY_LABELS: Record<string, string> = {
  general: '🏆 General',
  offense: '🏈 Offense',
  defense: '🛡️ Defense',
  rules: '📋 Rules',
  tactics: '⚽ Tactics',
  setpieces: '🥅 Set Pieces',
};

const FOOTBALL_CATEGORY_ORDER = ['general', 'offense', 'defense', 'rules'];
const SOCCER_CATEGORY_ORDER = ['general', 'tactics', 'setpieces'];

export function AchievementsGrid() {
  const { user } = useAuth();
  const { preferredSport } = usePreferredSport();
  const [earnedIds, setEarnedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_achievements')
      .select('achievement_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (data) setEarnedIds(new Set(data.map(r => r.achievement_id)));
      });
  }, [user]);

  const sportAchievements = getAchievementsForSport(preferredSport);
  const categoryOrder = preferredSport === 'soccer' ? SOCCER_CATEGORY_ORDER : FOOTBALL_CATEGORY_ORDER;

  const grouped = categoryOrder
    .map(cat => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      items: sportAchievements.filter(a => a.category === cat),
    }))
    .filter(g => g.items.length > 0);

  const totalEarned = sportAchievements.filter(a => earnedIds.has(a.id)).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">Achievements</h3>
        <span className="text-sm font-display font-bold text-accent">
          {totalEarned}/{sportAchievements.length}
        </span>
      </div>

      {grouped.map(group => (
        <div key={group.category}>
          <p className="text-sm font-display font-semibold text-muted-foreground mb-2">
            {group.label}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {group.items.map((achievement, i) => {
              const earned = earnedIds.has(achievement.id);
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex flex-col items-center text-center p-3 rounded-xl border-2 transition-all ${
                    earned
                      ? 'bg-accent/10 border-accent/40 shadow-card'
                      : 'bg-muted/30 border-border opacity-50'
                  }`}
                >
                  <span className={`text-2xl mb-1 ${earned ? '' : 'grayscale'}`}>
                    {achievement.icon}
                  </span>
                  <p className="font-display font-bold text-xs leading-tight">
                    {achievement.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                    {achievement.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
